import React, { FC, useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AgentThread,
  GetCampaignProspectsWithRepliesResponse,
  CampaignErrorResponse,
} from '@dreamai/types';
import api from '../../../api';
import UnreadIndicator from './UnreadIndicator';
import './thread-item.scss';

interface ThreadItemProps {
  thread: AgentThread;
  selectedThreadId?: string | null;
  onThreadSelect: (
    threadId: string,
    agentId?: string,
    readMarker?: string,
  ) => void;
  discussingObject: string;
  isUnread: boolean;
  createReadMarker: (threadId: string) => string | undefined;
  getDiscussingObjectIcon: (discussingObject: string) => React.ReactNode | null;
  onSelect?: () => void; // Optional callback after selection (e.g., close drawer)
}

const ThreadItem: FC<ThreadItemProps> = ({
  thread,
  selectedThreadId,
  onThreadSelect,
  discussingObject,
  isUnread,
  createReadMarker,
  getDiscussingObjectIcon,
  onSelect,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [hasNewReplies, setHasNewReplies] = useState(false);

  // Check if campaign has new replies
  useEffect(() => {
    const checkCampaignReplies = async () => {
      if (
        discussingObject.toLowerCase() !== 'campaign' ||
        !thread.discussing_objects
      ) {
        return;
      }

      // Find the campaign object in discussing_objects
      const campaignObject = thread.discussing_objects.find(
        (obj) => obj.discussing_object.toLowerCase() === 'campaign',
      );

      const campaignId = campaignObject?.discussing_object_match?.campaignId;
      if (!campaignId) {
        console.log('hizza', campaignObject);
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.get<
          GetCampaignProspectsWithRepliesResponse | CampaignErrorResponse
        >(`/campaigns/${campaignId}/prospects-with-replies`);

        if (response.data.success) {
          // Check if any prospects have unread replies
          const hasUnread = response.data.prospects.some(
            (p) => p.hasUnreadReplies,
          );
          setHasNewReplies(hasUnread);
        }
      } catch (error) {
        console.error('Error checking campaign replies:', error);
      }
    };

    checkCampaignReplies();
  }, [discussingObject, thread.discussing_objects, getAccessTokenSilently]);
  return (
    <li className={`thread-item-wrapper ${isUnread ? 'unread' : ''}`}>
      <a
        href={`?agent_id=${thread.agent_id}&thread_id=${thread.id}`}
        className={`thread-item ${selectedThreadId === thread.id ? 'active' : ''}`}
        onClick={(e) => {
          // Only prevent default if not opening in new tab
          if (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.button === 0) {
            e.preventDefault();
            onThreadSelect(
              thread.id,
              thread.agent_id,
              createReadMarker(thread.id),
            );
            onSelect?.();
          }
        }}
      >
        <div className='category-indicator'>
          <div
            className={`category-indicator-inside ${discussingObject.toLowerCase()} ${thread.discussing_object_pills?.[0].color || 'standard'}`}
          >
            {getDiscussingObjectIcon(discussingObject)}
          </div>
        </div>
        <div className='text-wrapper'>
          <div className='thread-title'>
            {thread.description ||
              `Conversation ${new Date(thread.created_at).toLocaleDateString()}`}
          </div>
          {((thread.discussing_object_pills &&
            thread.discussing_object_pills.length > 0) ||
            hasNewReplies) && (
            <div className='thread-pills'>
              {hasNewReplies && (
                <span className='pill magic'>🎉 New reply</span>
              )}
              {thread.discussing_object_pills?.map((pill) => (
                <span key={pill.value} className={`pill ${pill.color}`}>
                  {pill.value}
                </span>
              ))}
            </div>
          )}
        </div>
        {isUnread && <UnreadIndicator />}
      </a>
    </li>
  );
};

export default ThreadItem;
